import { useEffect, useState } from "react";
import { apiGet } from "../lib/api";
import { Card, Label, Pill } from "../components/ui";

type T = { name: string; missing: boolean };
type App = {
  name: string;
  script: string;
  live: boolean | null;
  ownDb: string;
  alsoReads: string[];
  writes: T[];
  reads: T[];
  private: T[];
  staysOwn: string[];
};
type Outside = { name: string; db: string; tables: string[]; note: string | null };
type Data = { apps: App[]; outside: Outside[]; liveCount: number; liveKnown: boolean };

export default function DataMapScreen() {
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<Data>("/api/admin/datamap")
      .then(setData)
      .catch((e) => setError(String(e.message ?? e)));
  }, []);

  if (error) return <p className="text-sm text-brand">{error}</p>;
  if (!data) return <p className="text-sm text-neutral-400">Loading…</p>;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl text-ink">Data map</h1>
        <Pill tone={data.liveCount === data.apps.length ? "ok" : data.liveCount > 0 ? "amber" : "grey"}>
          {data.liveCount} of {data.apps.length} apps live on Core
        </Pill>
        {!data.liveKnown && <Pill tone="amber">live state unavailable for some apps</Pill>}
        <span className="text-sm text-neutral-400">where each app keeps its data right now</span>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {data.apps.map((a) => (
          <AppCard key={a.script} app={a} />
        ))}
        <Card className="border-dashed bg-paper">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[15px] font-bold text-ink">Outside Core</span>
            <Pill tone="red">{data.outside.length} databases</Pill>
          </div>
          {data.outside.map((o) => (
            <div key={o.db}>
              <Section label={`${o.name} · ${o.db}`}>
                <Tables items={o.tables.map((name) => ({ name, missing: false }))} />
              </Section>
              {o.note && <p className="mt-1 text-xs text-brand">{o.note}</p>}
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

function AppCard({ app: a }: { app: App }) {
  const onCore = a.live === true;
  const outsideDbs = onCore ? [] : a.alsoReads;
  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[15px] font-bold text-ink">{a.name}</span>
        {a.live === null ? (
          <Pill tone="amber">unknown</Pill>
        ) : onCore ? (
          <Pill tone="ok">● live on Core</Pill>
        ) : (
          <Pill tone="grey">● not live</Pill>
        )}
      </div>
      <p className="mt-1 text-xs text-neutral-600">
        Reading from <b className="text-brand">{onCore ? "gails-core" : a.ownDb}</b>
        {onCore ? "" : " (own)"}
        {outsideDbs.length > 0 && <> + {outsideDbs.join(", ")}</>}
      </p>

      {a.writes.length > 0 && (
        <Section label={onCore ? "Writes via Core" : "Writes via Core, once live"}>
          <Tables items={a.writes} />
        </Section>
      )}
      {a.reads.length > 0 && (
        <Section label={a.writes.length ? "Also reads" : "Reads only"}>
          <Tables items={a.reads} collapseAfter={6} />
        </Section>
      )}
      {a.private.length > 0 && (
        <Section label="Logins & private">
          <Tables items={a.private} />
        </Section>
      )}
      {a.staysOwn.length > 0 && (
        <Section label={`Stays in ${a.ownDb}`}>
          <Tables items={a.staysOwn.map((name) => ({ name, missing: false }))} />
        </Section>
      )}
    </Card>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-3">
      <Label>{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Tables({ items, collapseAfter }: { items: T[]; collapseAfter?: number }) {
  const [open, setOpen] = useState(false);
  const shown = collapseAfter && !open ? items.slice(0, collapseAfter) : items;
  const hidden = items.length - shown.length;
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((t) => (
        <code
          key={t.name}
          title={t.missing ? "Not found in gails-core" : undefined}
          className={`rounded px-1.5 py-0.5 text-[11px] ${
            t.missing ? "bg-red-50 text-brand line-through" : "bg-neutral-100 text-neutral-600"
          }`}
        >
          {t.name}
        </code>
      ))}
      {hidden > 0 && (
        <button className="min-h-[24px] px-1 text-[11px] font-semibold text-brand" onClick={() => setOpen(true)}>
          +{hidden} more
        </button>
      )}
    </div>
  );
}
